"use client"
import { IoSearch } from "react-icons/io5"
import { RiCloseLine, RiInformationLine, RiMapPinLine } from "@remixicon/react"
import { AnimatePresence, motion } from "framer-motion"
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { format, isAfter, isSameDay, startOfToday } from "date-fns"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import LocationDropdown from "@/screens/dashboard/ui/LocationDropdownNew"
import DateRangePicker from "@/screens/dashboard/ui/DateRangePicker"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import GuestSelector from "@/screens/dashboard/ui/GuestSelector"
import { useGuestSelection } from "@/hooks/useGuestSelection"
import { useDateRange } from "@/hooks/useDateRange"
import { useLocation } from "@/hooks/useLocation"
import { toast } from "react-toastify"
import { OPEN_LISTING_SEARCH_DATES_EVENT } from "./events"

const FLEXIBLE_OPTIONS = [
  { label: "Exact dates", value: "" },
  { label: "± 1 day", value: "1" },
  { label: "± 2 days", value: "2" },
  { label: "± 3 days", value: "3" },
  { label: "± 7 days", value: "7" },
]

const STAYS_ACCENT = "#80C7E2"
const STAYS_ACCENT_SOFT = "#F2FBFE"
const STAYS_ACCENT_SOFT_HOVER = "#E8F7FC"
const STAYS_TEXT_DARK = "#000D33"
const STAYS_TEXT_MUTED = "#55656F"
const FLEXIBLE_DATES_TOOLTIP =
  "Flexible dates expands your search to include stays that can start or end a few days before or after the dates you pick."
const PANEL_EASE = [0.22, 1, 0.36, 1]
const DESKTOP_SMALL_PANEL_WIDTH = 392
const DESKTOP_PANEL_OPEN_TRANSITION = {
  duration: 0.48,
  ease: [0.12, 1, 0.26, 1],
}
const DESKTOP_PANEL_SWITCH_EXPAND_TRANSITION = {
  duration: 0.6,
  ease: [0.12, 1, 0.24, 1],
}
const DESKTOP_PANEL_SWITCH_SHRINK_TRANSITION = {
  duration: 0.74,
  ease: [0.16, 1, 0.24, 1],
}
const DESKTOP_PANEL_REVEAL_TRANSITION = {
  duration: 0.38,
  ease: [0.18, 1, 0.3, 1],
}
const DESKTOP_PANEL_HEIGHT_OPEN_TRANSITION = {
  duration: 0.46,
  ease: [0.14, 1, 0.24, 1],
}
const DESKTOP_PANEL_HEIGHT_EXPAND_TRANSITION = {
  duration: 0.5,
  ease: [0.14, 1, 0.24, 1],
}
const DESKTOP_PANEL_HEIGHT_SHRINK_TRANSITION = {
  duration: 0.62,
  ease: [0.16, 1, 0.22, 1],
}
const FLEXIBLE_TOOLTIP_WIDTH = 232
const FLEXIBLE_TOOLTIP_OFFSET = 10
const DESKTOP_PANEL_CONTENT_ENTER_TRANSITION = {
  duration: 0.24,
  ease: [0.18, 1, 0.3, 1],
}
const DESKTOP_LOCATION_PANEL_MAX_HEIGHT = 236
const DESKTOP_LOCATION_PANEL_FALLBACK_HEIGHT = 216
const GUESTS_AUTO_OPEN_DELAY_MS = 180
const DESKTOP_PANEL_WIDTHS = {
  location: DESKTOP_SMALL_PANEL_WIDTH,
  dates: 980,
  guests: DESKTOP_SMALL_PANEL_WIDTH,
}
const DESKTOP_PANEL_HEIGHT_FALLBACKS = {
  location: DESKTOP_LOCATION_PANEL_FALLBACK_HEIGHT,
  dates: 454,
  guests: 270,
}
const DESKTOP_PANEL_ORDER = {
  location: 0,
  dates: 1,
  guests: 2,
}
const DESKTOP_GUEST_TYPES = [
  {
    key: "adults",
    label: "Adults",
    description: "Ages 13 or above",
    minValue: 1,
  },
  {
    key: "children",
    label: "Children",
    description: "Ages 3-12",
    minValue: 0,
  },
]

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const isDesktopViewport = () => typeof window !== "undefined" && window.innerWidth >= 768
const isRenderedElementVisible = (element) => {
  if (typeof window === "undefined" || !element) {
    return false
  }

  const computedStyle = window.getComputedStyle(element)

  return (
    computedStyle.display !== "none" &&
    computedStyle.visibility !== "hidden" &&
    element.getClientRects().length > 0
  )
}

const mobileBackdropMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.18, ease: "easeOut" } },
  exit: { opacity: 0, transition: { duration: 0.14, ease: "easeIn" } },
}

const SearchGuestCounterButton = ({ onClick, disabled, children, ariaLabel }) => {
  const [isHovered, setIsHovered] = useState(false)
  const [isPressed, setIsPressed] = useState(false)

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className="h-8 w-8 cursor-pointer rounded-full border flex items-center justify-center text-[17px] disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        borderColor: isHovered ? STAYS_ACCENT : "#CFEAF4",
        backgroundColor: isPressed ? STAYS_ACCENT : isHovered ? STAYS_ACCENT_SOFT : "#FFFFFF",
        color: isPressed ? "#FFFFFF" : STAYS_TEXT_DARK,
        boxShadow: isHovered ? "0 6px 14px rgba(128, 199, 226, 0.18)" : "0 2px 6px rgba(10, 28, 44, 0.04)",
        transform: isPressed ? "scale(0.96)" : isHovered ? "translateY(-1px)" : "translateY(0)",
        transition: "background-color 150ms ease, border-color 150ms ease, color 150ms ease, box-shadow 150ms ease, transform 150ms ease",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false)
        setIsPressed(false)
      }}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
    >
      {children}
    </button>
  )
}

const DesktopGuestRow = ({ type, count, onUpdate, maxGuests, totalGuests }) => {
  const isIncrementDisabled = () => {
    if (type.key === "pets") {
      return count >= 2
    }

    if (type.key === "adults" || type.key === "children") {
      return totalGuests >= maxGuests
    }

    return false
  }

  return (
    <div
      className="flex min-h-[74px] items-center justify-between gap-3 rounded-[14px] px-0 py-3"
      style={{ backgroundColor: "#FFFFFF" }}
    >
      <div className="flex-1 pr-2.5">
        <div className="font-semibold text-[14px] leading-5" style={{ color: STAYS_TEXT_DARK }}>
          {type.label}
        </div>
        <div className="mt-0.5 text-[12px] leading-[17px]" style={{ color: STAYS_TEXT_MUTED }}>
          {type.description}
        </div>
      </div>
      <div
        className="flex items-center gap-2 rounded-full border px-2 py-1"
        style={{
          backgroundColor: "#F7FCFE",
          borderColor: "#DCECF3",
        }}
      >
        <SearchGuestCounterButton
          onClick={() => onUpdate(type.key, -1)}
          disabled={count <= type.minValue}
          ariaLabel={`Decrease ${type.label.toLowerCase()} count`}
        >
          -
        </SearchGuestCounterButton>
        <span className="w-6 text-center font-semibold text-[14px]" style={{ color: STAYS_TEXT_DARK }}>
          {count}
        </span>
        <SearchGuestCounterButton
          onClick={() => onUpdate(type.key, 1)}
          disabled={isIncrementDisabled()}
          ariaLabel={`Increase ${type.label.toLowerCase()} count`}
        >
          +
        </SearchGuestCounterButton>
      </div>
    </div>
  )
}

const ListingSearch = () => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const location = searchParams.get("location")
  const checkin = searchParams.get("checkin")
  const checkout = searchParams.get("checkout")
  const guests = searchParams.get("guests")
  const adults = searchParams.get("adults")
  const children = searchParams.get("children")
  const infants = searchParams.get("infants")
  const flex_days = searchParams.get("flex_days")
  const isPropertyListingPage = pathname?.includes("/property-listing")

  const [selectedLocation, setSelectedLocation] = useState(location || "")
  const [showGuestsDropdown, setShowGuestsDropdown] = useState(false)
  const [flexibleDate, setFlexibleDate] = useState(flex_days || "")
  const [isMounted, setIsMounted] = useState(false)
  const [hoveredSection, setHoveredSection] = useState(null)
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false)
  const [hoveredFlexOption, setHoveredFlexOption] = useState(null)
  const [hoveredLocationOption, setHoveredLocationOption] = useState(null)
  const [showFlexibleDatesTooltip, setShowFlexibleDatesTooltip] = useState(false)
  const [flexibleTooltipPosition, setFlexibleTooltipPosition] = useState(null)
  const [searchButtonHovered, setSearchButtonHovered] = useState(false)
  const [searchButtonPressed, setSearchButtonPressed] = useState(false)
  const [desktopActivePanel, setDesktopActivePanel] = useState(null)
  const [locationDropdownDismissSignal, setLocationDropdownDismissSignal] = useState(0)
  const [hasSearchInteraction, setHasSearchInteraction] = useState(
    Boolean(location || checkin || checkout || flex_days),
  )
  const [hasGuestValueSelection, setHasGuestValueSelection] = useState(
    Boolean(guests || adults || children || infants),
  )
  const [desktopPanelStyle, setDesktopPanelStyle] = useState({
    left: 8,
    width: DESKTOP_PANEL_WIDTHS.location,
  })
  const [desktopPanelAnimationMode, setDesktopPanelAnimationMode] = useState("opening")
  const [desktopPanelDirection, setDesktopPanelDirection] = useState(0)
  const [desktopPanelSizeTransitionMode, setDesktopPanelSizeTransitionMode] = useState("steady")
  const [desktopVisiblePanel, setDesktopVisiblePanel] = useState(null)
  const [desktopPanelHeight, setDesktopPanelHeight] = useState(
    DESKTOP_PANEL_HEIGHT_FALLBACKS.location,
  )
  const searchContainerRef = useRef(null)
  const locationSectionRef = useRef(null)
  const datesSectionRef = useRef(null)
  const guestsSectionRef = useRef(null)
  const guestsDropdownRef = useRef(null)
  const desktopPanelContentRef = useRef(null)
  const desktopPanelHeightsRef = useRef({ ...DESKTOP_PANEL_HEIGHT_FALLBACKS })
  const guestsAutoOpenTimeoutRef = useRef(null)
  const flexibleTooltipHideTimeoutRef = useRef(null)
  const { cities, loading: isLocationsLoading, isError: isLocationsError } = useLocation()

  const {
    startDate,
    endDate,
    isDateSelected,
    showDatePicker,
    activeInput,
    calendarRef,
    mobileCalendarRef,
    dateInputRef,
    dateRange,
    displayText,
    toggleDatePicker,
    closeDatePicker,
    handleStartDateChange: handleRangeStartDateChange,
    handleEndDateChange: handleRangeEndDateChange,
    handleDesktopDateChange,
    clearDates,
  } = useDateRange(checkin, checkout)

  const {
    guestCounts,
    totalGuests,
    updateGuestCount,
    getDisplayText: getGuestDisplayText,
  } = useGuestSelection(
    Number(guests) || 2,
    adults || children || infants
      ? { adults, children, infants }
      : null,
  )

  const isLocationActive = desktopActivePanel === "location" || locationDropdownOpen
  const isDatesActive = desktopActivePanel === "dates" || showDatePicker
  const isGuestsActive = desktopActivePanel === "guests" || showGuestsDropdown
  const shouldShowSearchLabel = Boolean(
    selectedLocation ||
    isDateSelected ||
    flexibleDate ||
    hasGuestValueSelection,
  )

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (!showDatePicker) {
      setShowFlexibleDatesTooltip(false)
    }
  }, [showDatePicker])

  useEffect(() => {
    if (!desktopActivePanel) {
      return undefined
    }

    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        closeDesktopPanel()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [desktopActivePanel])

  useEffect(() => {
    if (!showGuestsDropdown) return

    const handleClickOutside = (event) => {
      if (
        guestsDropdownRef.current &&
        !guestsDropdownRef.current.contains(event.target) &&
        !(event.target instanceof Element && event.target.closest('[data-guest-mobile-portal]'))
      ) {
        setShowGuestsDropdown(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [showGuestsDropdown])

  useEffect(() => {
    return () => {
      if (guestsAutoOpenTimeoutRef.current) {
        window.clearTimeout(guestsAutoOpenTimeoutRef.current)
      }

      if (flexibleTooltipHideTimeoutRef.current) {
        window.clearTimeout(flexibleTooltipHideTimeoutRef.current)
      }
    }
  }, [])

  useLayoutEffect(() => {
    if (!desktopVisiblePanel || !desktopPanelContentRef.current) {
      return undefined
    }

    const contentNode = desktopPanelContentRef.current

    const syncMeasuredHeight = () => {
      const nextHeight = contentNode.offsetHeight

      if (!nextHeight) {
        return
      }

      desktopPanelHeightsRef.current[desktopVisiblePanel] = nextHeight
      setDesktopPanelHeight(nextHeight)
    }

    syncMeasuredHeight()

    if (typeof ResizeObserver === "undefined") {
      return undefined
    }

    const resizeObserver = new ResizeObserver(() => {
      syncMeasuredHeight()
    })

    resizeObserver.observe(contentNode)

    return () => {
      resizeObserver.disconnect()
    }
  }, [desktopVisiblePanel])

  useEffect(() => {
    if (!showFlexibleDatesTooltip) {
      return undefined
    }

    const handleViewportChange = () => {
      setShowFlexibleDatesTooltip(false)
      setFlexibleTooltipPosition(null)
    }

    window.addEventListener("resize", handleViewportChange)
    window.addEventListener("scroll", handleViewportChange, true)

    return () => {
      window.removeEventListener("resize", handleViewportChange)
      window.removeEventListener("scroll", handleViewportChange, true)
    }
  }, [showFlexibleDatesTooltip])

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined
    }

    const handleOpenDatesRequest = () => {
      if (!isRenderedElementVisible(searchContainerRef.current)) {
        return
      }

      setHasSearchInteraction(true)
      setShowGuestsDropdown(false)
      setLocationDropdownOpen(false)
      setLocationDropdownDismissSignal((current) => current + 1)
      setHoveredSection(null)
      setShowFlexibleDatesTooltip(false)
      searchContainerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      })

      if (isDesktopViewport()) {
        if (desktopActivePanel !== "dates") {
          openDesktopPanel("dates")
        }

        return
      }

      if (!showDatePicker) {
        toggleDatePicker()
      }
    }

    window.addEventListener(OPEN_LISTING_SEARCH_DATES_EVENT, handleOpenDatesRequest)

    return () => {
      window.removeEventListener(OPEN_LISTING_SEARCH_DATES_EVENT, handleOpenDatesRequest)
    }
  }, [desktopActivePanel, showDatePicker, toggleDatePicker])

  const getDesktopPanelMetrics = (panelName) => {
    if (!searchContainerRef.current) {
      return null
    }

    const containerRect = searchContainerRef.current.getBoundingClientRect()
    const horizontalPadding = 8
    const maxWidth = Math.max(containerRect.width - horizontalPadding * 2, 280)

    const getMetrics = (sectionRect, preferredWidth, alignment = "center") => {
      if (!sectionRect) {
        return null
      }

      const width = Math.min(preferredWidth, maxWidth)
      const leftFromContainer = sectionRect.left - containerRect.left
      const sectionCenter = leftFromContainer + sectionRect.width / 2
      const sectionRight = leftFromContainer + sectionRect.width
      let nextLeft = sectionCenter - width / 2

      if (alignment === "start") {
        nextLeft = leftFromContainer
      }

      if (alignment === "end") {
        nextLeft = sectionRight - width
      }

      return {
        left: clamp(
          nextLeft,
          horizontalPadding,
          Math.max(horizontalPadding, containerRect.width - width - horizontalPadding),
        ),
        width,
      }
    }

    const sectionRectMap = {
      location: locationSectionRef.current?.getBoundingClientRect(),
      dates: datesSectionRef.current?.getBoundingClientRect(),
      guests: guestsSectionRef.current?.getBoundingClientRect(),
    }

    const metricsMap = {
      location: getMetrics(sectionRectMap.location, DESKTOP_PANEL_WIDTHS.location, "start"),
      dates: getMetrics(sectionRectMap.dates, DESKTOP_PANEL_WIDTHS.dates, "center"),
      guests: getMetrics(sectionRectMap.guests, DESKTOP_PANEL_WIDTHS.guests, "end"),
    }

    return metricsMap[panelName] || null
  }

  const syncDesktopPanelMetrics = (panelName) => {
    const nextMetrics = getDesktopPanelMetrics(panelName)

    if (!nextMetrics) {
      return
    }

    setDesktopPanelStyle((current) => {
      if (
        current.left === nextMetrics.left &&
        current.width === nextMetrics.width
      ) {
        return current
      }

      return nextMetrics
    })
  }

  const clearGuestsAutoOpenTimeout = () => {
    if (guestsAutoOpenTimeoutRef.current) {
      window.clearTimeout(guestsAutoOpenTimeoutRef.current)
      guestsAutoOpenTimeoutRef.current = null
    }
  }

  useLayoutEffect(() => {
    if (!desktopActivePanel) {
      return undefined
    }

    const updateDesktopPanelPosition = () => {
      syncDesktopPanelMetrics(desktopActivePanel)
    }

    updateDesktopPanelPosition()
    window.addEventListener("resize", updateDesktopPanelPosition)

    return () => window.removeEventListener("resize", updateDesktopPanelPosition)
  }, [desktopActivePanel])

  const openDesktopPanel = (panelName) => {
    clearGuestsAutoOpenTimeout()

    const previousPanel = desktopActivePanel
    const previousWidth = DESKTOP_PANEL_WIDTHS[previousPanel] ?? DESKTOP_SMALL_PANEL_WIDTH
    const targetWidth = DESKTOP_PANEL_WIDTHS[panelName] ?? DESKTOP_SMALL_PANEL_WIDTH
    const targetHeight =
      desktopPanelHeightsRef.current[panelName] ?? DESKTOP_PANEL_HEIGHT_FALLBACKS[panelName]

    syncDesktopPanelMetrics(panelName)
    setDesktopPanelHeight(targetHeight)

    if (!previousPanel) {
      setDesktopPanelAnimationMode("opening")
      setDesktopPanelDirection(0)
      setDesktopPanelSizeTransitionMode("steady")
      setDesktopVisiblePanel(panelName)
    } else {
      setDesktopPanelAnimationMode("switching")
      const direction = Math.sign(
        (DESKTOP_PANEL_ORDER[panelName] ?? 0) - (DESKTOP_PANEL_ORDER[previousPanel] ?? 0),
      )
      const sizeTransitionMode =
        targetWidth < previousWidth
          ? "shrink"
          : targetWidth > previousWidth
            ? "expand"
            : "steady"

      setDesktopPanelDirection(direction || 1)
      setDesktopPanelSizeTransitionMode(sizeTransitionMode)
      setDesktopVisiblePanel(panelName)
    }

    setDesktopActivePanel(panelName)
  }

  const closeDesktopPanel = () => {
    clearGuestsAutoOpenTimeout()
    setDesktopActivePanel(null)
    setShowFlexibleDatesTooltip(false)
  }

  const closeSearchDropdowns = () => {
    closeDesktopPanel()
    closeDatePicker()
    setShowGuestsDropdown(false)
    setLocationDropdownOpen(false)
    setLocationDropdownDismissSignal((current) => current + 1)
    setHoveredSection(null)
  }

  const buildSearchParams = useCallback(({
    locationValue = selectedLocation,
    startDateValue = startDate,
    endDateValue = endDate,
    guestCountsValue = guestCounts,
    flexibleDateValue = flexibleDate,
  } = {}) => {
    const params = new URLSearchParams()
    if (locationValue) {
      params.set("location", locationValue)
    }

    if (startDateValue && endDateValue) {
      params.set("checkin", format(startDateValue, "yyyy-MM-dd"))
      params.set("checkout", format(endDateValue, "yyyy-MM-dd"))
    }

    const totalGuestsValue =
      (guestCountsValue?.adults || 0) + (guestCountsValue?.children || 0)

    if (totalGuestsValue > 0) {
      params.set("guests", totalGuestsValue.toString())
      params.set("adults", (guestCountsValue?.adults || 0).toString())
      params.set("children", (guestCountsValue?.children || 0).toString())
      params.set("infants", (guestCountsValue?.infants || 0).toString())
    }

    if (flexibleDateValue) {
      params.set("flex_days", flexibleDateValue)
    }

    return params
  }, [endDate, flexibleDate, guestCounts, selectedLocation, startDate])

  const validateDateValues = useCallback((startDateValue, endDateValue) => {
    const hasStartDate = Boolean(startDateValue)
    const hasEndDate = Boolean(endDateValue)

    if (hasStartDate !== hasEndDate) {
      toast.warn("Please select both check-in and check-out dates")
      return false
    }

    if (hasStartDate && hasEndDate) {
      if (isSameDay(startDateValue, endDateValue)) {
        toast.warn("Check-in and check-out dates cannot be the same")
        return false
      }

      if (!isAfter(endDateValue, startDateValue)) {
        toast.warn("Check-out date must be after check-in date")
        return false
      }
    }

    return true
  }, [])

  const submitSearch = useCallback(({
    locationValue = selectedLocation,
    startDateValue = startDate,
    endDateValue = endDate,
    guestCountsValue = guestCounts,
    flexibleDateValue = flexibleDate,
  } = {}) => {
    if (!validateDateValues(startDateValue, endDateValue)) {
      return false
    }

    setHasSearchInteraction(true)
    closeSearchDropdowns()

    const params = buildSearchParams({
      locationValue,
      startDateValue,
      endDateValue,
      guestCountsValue,
      flexibleDateValue,
    })

    router.push(`/property-listing?${params.toString()}`)

    return true
  }, [buildSearchParams, closeSearchDropdowns, endDate, flexibleDate, guestCounts, router, selectedLocation, startDate, validateDateValues])

  const handleSearch = useCallback(() => {
    submitSearch()
  }, [submitSearch])

  const handleGuestCountChange = (guestType, change) => {
    setHasSearchInteraction(true)
    setHasGuestValueSelection(true)

    const nextGuestCount = Math.max(0, (guestCounts[guestType] || 0) + change)
    if (guestType === "adults" && nextGuestCount === 0) {
      return
    }

    const nextGuestCounts = {
      ...guestCounts,
      [guestType]: nextGuestCount,
    }

    updateGuestCount(guestType, change)

    if (isPropertyListingPage) {
      submitSearch({ guestCountsValue: nextGuestCounts })
    }
  }

  const toggleDesktopPanel = (panelName) => {
    setHasSearchInteraction(true)

    if (desktopActivePanel === panelName) {
      closeDesktopPanel()
      return
    }

    openDesktopPanel(panelName)
  }

  const handleDesktopLocationSectionClick = () => {
    if (!isDesktopViewport()) {
      return
    }

    toggleDesktopPanel("location")
  }

  const handleLocationChange = (city) => {
    setHasSearchInteraction(true)
    setSelectedLocation(city)

    if (isPropertyListingPage) {
      submitSearch({ locationValue: city || "" })
      return
    }

    if (!city) {
      return
    }

    if (isDesktopViewport()) {
      openDesktopPanel("dates")
      return
    }

    if (!isDateSelected) {
      window.requestAnimationFrame(() => {
        toggleDatePicker()
      })
    }
  }

  const handleDesktopDateChangeWithAdvance = (range) => {
    setHasSearchInteraction(true)
    const hasCompleteRange = handleDesktopDateChange(range)

    if (hasCompleteRange) {
      if (isPropertyListingPage) {
        submitSearch({
          startDateValue: range.selection.startDate,
          endDateValue: range.selection.endDate,
        })
        return
      }

      clearGuestsAutoOpenTimeout()
      guestsAutoOpenTimeoutRef.current = window.setTimeout(() => {
        if (isDesktopViewport()) {
          openDesktopPanel("guests")
          guestsAutoOpenTimeoutRef.current = null
          return
        }

        setShowGuestsDropdown(true)
        guestsAutoOpenTimeoutRef.current = null
      }, GUESTS_AUTO_OPEN_DELAY_MS)
    }
  }

  const handleClearDates = () => {
    setHasSearchInteraction(true)
    clearDates()
    setFlexibleDate("")
  }

  const handleClearLocation = () => {
    setHasSearchInteraction(true)
    setSelectedLocation("")

    if (isPropertyListingPage) {
      submitSearch({ locationValue: "" })
    }
  }

  const handleMobileStartDateChange = (date) => {
    setHasSearchInteraction(true)

    const shouldAutoApplyExistingRange =
      isPropertyListingPage && endDate && isAfter(endDate, date)

    handleRangeStartDateChange(date)

    if (shouldAutoApplyExistingRange) {
      submitSearch({
        startDateValue: date,
        endDateValue: endDate,
      })
    }
  }

  const handleMobileEndDateChange = (date) => {
    setHasSearchInteraction(true)
    const hasCompleteRange = handleRangeEndDateChange(date)

    if (isPropertyListingPage && hasCompleteRange && startDate && isAfter(date, startDate)) {
      submitSearch({
        startDateValue: startDate,
        endDateValue: date,
      })
    }
  }

  const handleDateSectionClick = () => {
    setHasSearchInteraction(true)

    if (isDesktopViewport()) {
      toggleDesktopPanel("dates")
      return
    }

    toggleDatePicker()
  }

  const handleGuestTrigger = () => {
    setHasSearchInteraction(true)

    if (isDesktopViewport()) {
      toggleDesktopPanel("guests")
      return
    }

    setShowGuestsDropdown((prev) => !prev)
  }

  const handleDesktopGuestSectionClick = () => {
    if (!isDesktopViewport()) {
      return
    }

    handleGuestTrigger()
  }

  const onKeyUpSearchField = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleSearch()
    }
  }

  const getSectionStyle = (section, isActive = false) => {
    const isHovered = hoveredSection === section

    if (isActive) {
      return {
        backgroundColor: STAYS_ACCENT_SOFT,
        boxShadow: "inset 0 0 0 1px rgba(128, 199, 226, 0.42)",
      }
    }

    if (isHovered) {
      return {
        backgroundColor: STAYS_ACCENT_SOFT_HOVER,
      }
    }

    return undefined
  }

  const getFlexPillStyle = (value) => {
    const isSelected = flexibleDate === value
    const isHovered = hoveredFlexOption === value

    return {
      background: isSelected
        ? "linear-gradient(135deg, #80C7E2 0%, #63B9D9 100%)"
        : isHovered
          ? "linear-gradient(180deg, #F8FCFE 0%, #EEF8FC 100%)"
          : "#FFFFFF",
      borderColor: isSelected ? "#63B9D9" : isHovered ? "#B8E3F1" : "#D8E7EE",
      color: isSelected ? "#FFFFFF" : STAYS_TEXT_DARK,
      transform: isHovered && !isSelected ? "translateY(-1px)" : "translateY(0)",
      boxShadow: isSelected
        ? "0 14px 28px rgba(128, 199, 226, 0.28)"
        : isHovered
          ? "0 10px 20px rgba(10, 28, 44, 0.06)"
          : "0 2px 6px rgba(10, 28, 44, 0.03)",
      transition:
        "background-color 160ms ease, border-color 160ms ease, color 160ms ease, transform 160ms ease, box-shadow 160ms ease",
    }
  }

  const getLocationOptionStyle = (optionKey, cityName) => {
    const isSelected = selectedLocation === cityName
    const isHovered = hoveredLocationOption === optionKey

    return {
      background: isSelected
        ? "#F2FBFE"
        : isHovered
          ? "#F8FCFE"
          : "#FFFFFF",
      borderColor: isSelected ? "#B7E2EF" : isHovered ? "#E1F0F6" : "#EDF5F8",
      boxShadow: isSelected
        ? "0 8px 18px rgba(128, 199, 226, 0.12)"
        : isHovered
          ? "0 4px 12px rgba(10, 28, 44, 0.04)"
          : "none",
      transform: isHovered ? "translateY(-0.5px)" : "translateY(0)",
      transition: "background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
    }
  }

  const getLocationIconTileStyle = (optionKey, cityName) => {
    const isSelected = selectedLocation === cityName
    const isHovered = hoveredLocationOption === optionKey

    return {
      backgroundColor: isSelected || isHovered ? "#FFFFFF" : "#F7FBFD",
      borderColor: isSelected ? "#D0ECF6" : isHovered ? "#DCEFF6" : "#E6F1F6",
      color: STAYS_TEXT_DARK,
      boxShadow: isSelected
        ? "0 8px 16px rgba(128, 199, 226, 0.14)"
        : isHovered
          ? "0 6px 12px rgba(10, 28, 44, 0.04)"
          : "none",
      transition: "background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease",
    }
  }

  const getSearchButtonStyle = () => {
    const scale = searchButtonPressed ? 0.97 : searchButtonHovered ? 1.03 : 1

    return {
      background: "linear-gradient(135deg, #80C7E2 0%, #63B9D9 100%)",
      border: "1px solid rgba(255, 255, 255, 0.24)",
      transform: `scale(${scale})`,
      boxShadow: searchButtonHovered
        ? "0 10px 24px rgba(128, 199, 226, 0.34)"
        : "0 4px 12px rgba(128, 199, 226, 0.2)",
      transition: "transform 180ms ease, box-shadow 180ms ease, filter 180ms ease",
      filter: searchButtonHovered ? "brightness(0.96)" : "none",
    }
  }

  const clearFlexibleTooltipHideTimeout = () => {
    if (flexibleTooltipHideTimeoutRef.current) {
      window.clearTimeout(flexibleTooltipHideTimeoutRef.current)
      flexibleTooltipHideTimeoutRef.current = null
    }
  }

  const scheduleFlexibleTooltipHide = () => {
    clearFlexibleTooltipHideTimeout()
    flexibleTooltipHideTimeoutRef.current = window.setTimeout(() => {
      setShowFlexibleDatesTooltip(false)
      setFlexibleTooltipPosition(null)
      flexibleTooltipHideTimeoutRef.current = null
    }, 90)
  }

  const openFlexibleTooltip = (target, align = "center") => {
    if (!(target instanceof Element)) {
      return
    }

    clearFlexibleTooltipHideTimeout()

    const rect = target.getBoundingClientRect()
    const viewportPadding = 12
    let left = rect.left + rect.width / 2 - FLEXIBLE_TOOLTIP_WIDTH / 2

    if (align === "start") {
      left = rect.left
    }

    left = clamp(
      left,
      viewportPadding,
      Math.max(viewportPadding, window.innerWidth - FLEXIBLE_TOOLTIP_WIDTH - viewportPadding),
    )

    setFlexibleTooltipPosition({
      left,
      top: rect.bottom + FLEXIBLE_TOOLTIP_OFFSET,
    })
    setShowFlexibleDatesTooltip(true)
  }

  const toggleFlexibleTooltip = (event, align = "center") => {
    event.stopPropagation()

    if (showFlexibleDatesTooltip) {
      clearFlexibleTooltipHideTimeout()
      setShowFlexibleDatesTooltip(false)
      setFlexibleTooltipPosition(null)
      return
    }

    openFlexibleTooltip(event.currentTarget, align)
  }

  const renderFlexibleDatesHeader = ({ compact = false } = {}) => (
    <div className={compact ? "mb-3" : "mb-2.5"}>
      <div className={`relative flex items-center gap-2 ${compact ? "justify-start" : "justify-center"}`}>
        <span className={`block font-semibold font-sans ${compact ? "text-[12px]" : "text-[12px] text-center"}`} style={{ color: STAYS_TEXT_DARK }}>
          Flexible dates
        </span>
        <button
          type="button"
          aria-label="What flexible dates means"
          onClick={(e) => toggleFlexibleTooltip(e, compact ? "start" : "center")}
          onMouseEnter={(e) => openFlexibleTooltip(e.currentTarget, compact ? "start" : "center")}
          onMouseLeave={scheduleFlexibleTooltipHide}
          onFocus={(e) => openFlexibleTooltip(e.currentTarget, compact ? "start" : "center")}
          onBlur={scheduleFlexibleTooltipHide}
          className="inline-flex cursor-pointer select-none items-center justify-center bg-transparent p-0 text-[#5AA9C8] transition-colors duration-150 hover:text-[#3C97BB]"
        >
          <RiInformationLine size={14} />
        </button>
      </div>
    </div>
  )

  const renderFlexibleDateOptions = ({ compact = false } = {}) => (
    <div className={compact ? "flex flex-col gap-2" : "flex flex-wrap justify-center gap-2"}>
      {FLEXIBLE_OPTIONS.map((opt) => {
        const isSelected = flexibleDate === opt.value

        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => {
              setHasSearchInteraction(true)
              setFlexibleDate(opt.value)

              if (isPropertyListingPage && startDate && endDate && isAfter(endDate, startDate)) {
                submitSearch({ flexibleDateValue: opt.value })
              }
            }}
            onMouseEnter={() => setHoveredFlexOption(opt.value)}
            onMouseLeave={() => setHoveredFlexOption((current) => (current === opt.value ? null : current))}
            className={compact
              ? "w-full px-3.5 py-2.5 rounded-[16px] border cursor-pointer text-center"
              : "px-3.5 py-2 rounded-full border cursor-pointer text-center"
            }
            style={getFlexPillStyle(opt.value)}
          >
            <span
              className={`block font-sans font-semibold ${compact ? "text-[13px] leading-5" : "text-[13px] leading-5"}`}
              style={{ color: isSelected ? "#FFFFFF" : STAYS_TEXT_DARK }}
            >
              {opt.label}
            </span>
          </button>
        )
      })}
    </div>
  )

  const renderDesktopLocationContent = () => {
    if (isLocationsLoading) {
      return (
        <div className="p-4 text-[15px] font-medium font-sans" style={{ color: STAYS_TEXT_MUTED }}>
          Loading...
        </div>
      )
    }

    if (isLocationsError) {
      return (
        <div className="p-4 text-[15px] font-medium text-red-500 font-sans">
          Failed to load locations
        </div>
      )
    }

    if (!cities?.data?.length) {
      return (
        <div className="p-4 text-[14px] font-medium font-sans" style={{ color: STAYS_TEXT_MUTED }}>
          No locations found
        </div>
      )
    }

    return (
      <div
        className="flex max-h-[236px] flex-col gap-1 overflow-y-auto pr-1"
        style={{
          maxHeight: DESKTOP_LOCATION_PANEL_MAX_HEIGHT,
          scrollbarWidth: "thin",
          scrollbarColor: "#CFEAF4 transparent",
        }}
      >
        {cities.data.map((option, index) => {
          const optionKey = `${option?.id}-${index}`
          const isSelected = selectedLocation === option?.city_name

          return (
            <button
              key={optionKey}
              type="button"
              className="flex min-h-[56px] w-full cursor-pointer items-center gap-2.5 rounded-[12px] px-0 py-2 text-left transition-all duration-180"
              style={getLocationOptionStyle(optionKey, option?.city_name)}
              onMouseEnter={() => setHoveredLocationOption(optionKey)}
              onMouseLeave={() => setHoveredLocationOption((current) => (current === optionKey ? null : current))}
              onClick={() => handleLocationChange(option?.city_name)}
            >
              <div
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[12px] border"
                style={getLocationIconTileStyle(optionKey, option?.city_name)}
              >
                <RiMapPinLine size={20} />
              </div>
              <div className="flex flex-col min-w-0 flex-1">
                <span className="truncate font-semibold text-[13px] leading-[18px] text-[#191919]">
                  {option?.city_name}
                </span>
                <span className="truncate text-[11px] leading-4" style={{ color: STAYS_TEXT_MUTED }}>
                  {option?.title} {option?.country}
                </span>
              </div>
              {isSelected && (
                <span
                  className="flex-shrink-0 rounded-full px-1.5 py-1 text-[9px] font-semibold"
                  style={{ backgroundColor: "#EAF8FC", color: "#4A9DBD" }}
                >
                  Selected
                </span>
              )}
            </button>
          )
        })}
      </div>
    )
  }

  const renderDesktopDatesContent = () => (
    <div className="flex items-start gap-4 w-full">
      <div
        className="w-[184px] shrink-0 rounded-[22px] border p-3.5"
        style={{
          borderColor: "#EAF6FB",
          background: "linear-gradient(180deg, #FCFEFF 0%, #F6FBFD 100%)",
        }}
      >
        {renderFlexibleDatesHeader({ compact: true })}
        {renderFlexibleDateOptions({ compact: true })}
      </div>
      <div className="min-w-0 flex-1 rounded-[22px] border overflow-hidden" style={{ borderColor: "#EAF6FB" }}>
        <DateRangePicker
          dateRange={dateRange}
          onChange={handleDesktopDateChangeWithAdvance}
          excludeDateIntervals={[]}
          onKeyUp={onKeyUpSearchField}
          showSummary={false}
          classNameSize="min-w-[42rem]"
        />
      </div>
    </div>
  )

  const renderDesktopGuestsContent = () => (
    <div className="space-y-0">
      {DESKTOP_GUEST_TYPES.map((guestType) => (
        <DesktopGuestRow
          key={guestType.key}
          type={guestType}
          count={guestCounts[guestType.key]}
          onUpdate={handleGuestCountChange}
          maxGuests={10}
          totalGuests={totalGuests}
        />
      ))}
    </div>
  )

  const activeDesktopPanelContent = useMemo(() => {
    if (!desktopVisiblePanel) {
      return null
    }

    if (desktopVisiblePanel === "location") {
      return renderDesktopLocationContent()
    }

    if (desktopVisiblePanel === "dates") {
      return renderDesktopDatesContent()
    }

    return renderDesktopGuestsContent()
  }, [
    cities,
    dateRange,
    desktopVisiblePanel,
    flexibleDate,
    guestCounts,
    hoveredFlexOption,
    isLocationsError,
    isLocationsLoading,
    selectedLocation,
    showFlexibleDatesTooltip,
    totalGuests,
  ])

  const currentDesktopPanelMetrics = desktopActivePanel
    ? getDesktopPanelMetrics(desktopActivePanel) || desktopPanelStyle
    : desktopPanelStyle
  const desktopPanelContentWidth = Math.max(currentDesktopPanelMetrics.width - 32, 0)
  const desktopPanelPaddingClass =
    desktopVisiblePanel === "dates" ? "p-4" : "px-4 py-0"
  const desktopPanelMorphTransition =
    desktopPanelAnimationMode === "switching"
      ? desktopPanelSizeTransitionMode === "shrink"
        ? DESKTOP_PANEL_SWITCH_SHRINK_TRANSITION
        : DESKTOP_PANEL_SWITCH_EXPAND_TRANSITION
      : DESKTOP_PANEL_OPEN_TRANSITION
  const desktopPanelHeightTransition =
    desktopPanelAnimationMode === "switching"
      ? desktopPanelSizeTransitionMode === "shrink"
        ? DESKTOP_PANEL_HEIGHT_SHRINK_TRANSITION
        : DESKTOP_PANEL_HEIGHT_EXPAND_TRANSITION
      : DESKTOP_PANEL_HEIGHT_OPEN_TRANSITION
  const desktopPanelContentInitial =
    desktopPanelAnimationMode === "switching"
      ? {
          opacity: 0,
          x: desktopPanelDirection > 0 ? 14 : -14,
          y: 6,
          scale: 0.994,
        }
      : {
          opacity: 0,
          y: 8,
          scale: 0.996,
        }

  return (
    <div
      ref={searchContainerRef}
      className="select-none rounded-2xl md:rounded-full border border-[#E0E0E0] px-3 py-1 md:px-0 md:py-0 flex flex-col md:flex-row md:items-center gap-0 md:gap-0 w-full text-[14px] font-medium font-sans shadow-sm hover:shadow-md transition-shadow duration-200 relative bg-white"
      onMouseLeave={() => {
        setHoveredSection(null)
      }}
    >
      <div
        ref={locationSectionRef}
        className="flex cursor-pointer select-none items-center w-full md:flex-1 md:min-w-0 md:self-stretch px-1 py-2 md:px-6 md:py-3 gap-2 rounded-full transition-colors duration-200"
        style={getSectionStyle("location", isLocationActive)}
        onClick={handleDesktopLocationSectionClick}
        onMouseEnter={() => setHoveredSection("location")}
        onMouseLeave={() => setHoveredSection((current) => (current === "location" ? null : current))}
      >
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-[10px] md:text-[11px] font-semibold text-[#898989] md:text-[#191919] uppercase tracking-wide">
            Where
          </span>
          <button
            type="button"
            className={`hidden md:block w-full cursor-pointer select-none text-left bg-transparent border-none p-0 text-[14px] font-medium truncate ${
              selectedLocation ? "text-[#191919] font-semibold" : "text-[#898989]"
            }`}
            onClick={(e) => {
              e.stopPropagation()
              toggleDesktopPanel("location")
            }}
          >
            {selectedLocation || "Search destinations"}
          </button>
          <div className="md:hidden">
            <LocationDropdown
              className="w-full"
              value={selectedLocation}
              dropdownPosition="top-[40px] left-0"
              placeholder="Search destinations"
              onChange={handleLocationChange}
              dismissSignal={locationDropdownDismissSignal}
              showHeader={false}
              onOpenChange={(isOpen) => {
                setLocationDropdownOpen(isOpen)
                if (isOpen) {
                  setHasSearchInteraction(true)
                }
              }}
            />
          </div>
        </div>
        {selectedLocation && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              handleClearLocation()
            }}
            aria-label="Clear location"
            className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-[#7A8C96] hover:text-[#1E2C35] transition-colors cursor-pointer"
          >
            <RiCloseLine size={15} />
          </button>
        )}
      </div>

      <div className="h-[1px] md:h-8 w-full md:w-[1px] bg-[#E0E0E0] flex-shrink-0" />

      <div
        className="w-full md:flex-1 flex select-none items-center md:justify-center md:min-w-0 md:self-stretch px-1 py-2 md:px-6 md:py-3 cursor-pointer gap-2 rounded-full transition-colors duration-200"
        style={getSectionStyle("dates", isDatesActive)}
        ref={(node) => {
          datesSectionRef.current = node
          dateInputRef.current = node
        }}
        onClick={handleDateSectionClick}
        onMouseEnter={() => setHoveredSection("dates")}
        onMouseLeave={() => setHoveredSection((current) => (current === "dates" ? null : current))}
      >
        <div className="flex flex-col min-w-0 flex-1 md:text-center">
          <span className="text-[10px] md:text-[11px] font-semibold text-[#898989] md:text-[#191919] uppercase tracking-wide">
            When
          </span>
          <span
            className={`text-[13px] md:text-[14px] font-medium truncate ${
              displayText ? "text-[#191919]" : "text-[#898989]"
            }`}
          >
            {displayText
              ? flexibleDate
                ? `${displayText} (±${flexibleDate}d)`
                : displayText
              : "Select dates"}
          </span>
        </div>
        {isDateSelected && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              handleClearDates()
            }}
            aria-label="Clear dates"
            className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-[#7A8C96] hover:text-[#1E2C35] transition-colors cursor-pointer"
          >
            <RiCloseLine size={15} />
          </button>
        )}

        {isMounted &&
          createPortal(
            <AnimatePresence initial={false}>
              {showDatePicker && (
                <div
                  ref={mobileCalendarRef}
                  className="md:hidden"
                  onClick={(e) => e.stopPropagation()}
                >
                  <motion.div
                    key="listing-search-dates-backdrop"
                    className="fixed inset-0 z-[99998]"
                    style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
                    initial={mobileBackdropMotion.initial}
                    animate={mobileBackdropMotion.animate}
                    exit={mobileBackdropMotion.exit}
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleDatePicker()
                    }}
                  />
                  <div
                    className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleDatePicker()
                    }}
                  >
                    <motion.div
                      key="listing-search-dates-mobile"
                      className="bg-white rounded-[28px] shadow-2xl w-full max-w-[340px] overflow-hidden"
                      style={{ boxShadow: "0 22px 48px rgba(10, 28, 44, 0.18)" }}
                      initial={{ opacity: 0, y: 26, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 18, scale: 0.99 }}
                      transition={{ duration: 0.22, ease: PANEL_EASE }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex justify-end px-4 pt-4 pb-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleDatePicker()
                          }}
                          className="text-[#898989] text-sm font-medium cursor-pointer"
                        >
                          Done
                        </button>
                      </div>
                      <div className="px-3 pt-2 pb-3 border-b" style={{ borderColor: "#EAF6FB" }}>
                        {renderFlexibleDatesHeader()}
                        {renderFlexibleDateOptions()}
                      </div>
                      <div className="mobile-datepicker-wrapper px-2 pb-2">
                        {activeInput === "checkin" || (!startDate && !endDate) ? (
                          <DatePicker
                            selected={startDate}
                            onChange={handleMobileStartDateChange}
                            excludeDateIntervals={[]}
                            minDate={startOfToday()}
                            inline
                          />
                        ) : (
                          <DatePicker
                            selected={endDate}
                            onChange={handleMobileEndDateChange}
                            excludeDateIntervals={[]}
                            minDate={startOfToday()}
                            disabled={!startDate}
                            inline
                          />
                        )}
                      </div>
                    </motion.div>
                  </div>
                </div>
              )}
            </AnimatePresence>,
            document.body,
          )}
      </div>

      <div className="h-[1px] md:h-8 w-full md:w-[1px] bg-[#E0E0E0] flex-shrink-0" />

      <div
        ref={guestsSectionRef}
        className="w-full md:flex-1 flex cursor-pointer select-none items-center md:justify-center md:min-w-0 md:self-stretch px-1 py-2 md:px-6 md:py-3 relative rounded-full transition-colors duration-200 gap-2 md:gap-3"
        style={getSectionStyle("guests", isGuestsActive)}
        onClick={handleDesktopGuestSectionClick}
        onMouseEnter={() => setHoveredSection("guests")}
        onMouseLeave={() => setHoveredSection((current) => (current === "guests" ? null : current))}
      >
        <div className="flex flex-col min-w-0 w-full md:text-center flex-1">
          <span className="text-[10px] md:text-[11px] font-semibold text-[#898989] md:text-[#191919] uppercase tracking-wide">
            Who
          </span>
          <button
            type="button"
            className="hidden md:block w-full cursor-pointer select-none text-left md:text-center bg-transparent border-none p-0 text-[14px] font-medium truncate text-[#191919]"
            onClick={(e) => {
              e.stopPropagation()
              handleGuestTrigger()
            }}
          >
            {getGuestDisplayText()}
          </button>
          <div className="md:hidden w-full">
            <GuestSelector
              ref={guestsDropdownRef}
              isOpen={showGuestsDropdown}
              guestCounts={guestCounts}
              onUpdate={handleGuestCountChange}
              onToggle={handleGuestTrigger}
              getDisplayText={getGuestDisplayText}
              guestTypes={DESKTOP_GUEST_TYPES}
              dropdownClassName="top-[40px]"
              className="w-full"
              isListingPage={true}
              showHeader={false}
            />
          </div>
        </div>
        <button
          type="button"
          style={getSearchButtonStyle()}
          className={`flex select-none justify-center items-center rounded-full cursor-pointer border-none flex-shrink-0 ${shouldShowSearchLabel ? "px-4 py-2.5 md:px-4 md:py-2.5" : "p-2.5 md:p-3"}`}
          onClick={(e) => {
            e.stopPropagation()
            handleSearch()
          }}
          onMouseEnter={() => setSearchButtonHovered(true)}
          onMouseLeave={() => {
            setSearchButtonHovered(false)
            setSearchButtonPressed(false)
          }}
          onMouseDown={() => setSearchButtonPressed(true)}
          onMouseUp={() => setSearchButtonPressed(false)}
          aria-label="Search"
        >
          <IoSearch className="text-white text-lg md:text-xl flex-shrink-0" id="searchIdButton" />
          <span
            className="hidden md:block text-white text-[14px] font-semibold whitespace-nowrap overflow-hidden"
            style={{
              maxWidth: shouldShowSearchLabel ? "56px" : "0px",
              opacity: shouldShowSearchLabel ? 1 : 0,
              marginLeft: shouldShowSearchLabel ? "2px" : "0px",
              transition: "max-width 220ms ease, opacity 180ms ease, margin-left 220ms ease",
            }}
          >
            Search
          </span>
        </button>
      </div>

      <AnimatePresence initial={false}>
        {desktopActivePanel && activeDesktopPanelContent && (
          <motion.div
            ref={calendarRef}
            key="listing-search-desktop-panel"
            className={`hidden md:block absolute top-full z-50 bg-white rounded-2xl border mt-2 overflow-hidden ${desktopPanelPaddingClass}`}
            initial={
              desktopPanelAnimationMode === "opening"
                ? {
                    opacity: 0,
                    y: 5,
                    left: currentDesktopPanelMetrics.left,
                    width: currentDesktopPanelMetrics.width,
                    scale: 0.995,
                  }
                : false
            }
            animate={{
              opacity: 1,
              y: 0,
              left: currentDesktopPanelMetrics.left,
              width: currentDesktopPanelMetrics.width,
              height: desktopPanelHeight,
              scale: 1,
            }}
            exit={{
              opacity: 0,
              y: 3,
              left: currentDesktopPanelMetrics.left,
              width: currentDesktopPanelMetrics.width,
              height: desktopPanelHeight,
              scale: 0.997,
            }}
            transition={{
              opacity: { duration: 0.28, ease: "easeOut" },
              y: DESKTOP_PANEL_REVEAL_TRANSITION,
              scale: { duration: 0.42, ease: [0.14, 1, 0.28, 1] },
              left: desktopPanelMorphTransition,
              width: desktopPanelMorphTransition,
              height: desktopPanelHeightTransition,
            }}
            style={{
              borderColor: "#D8F1F8",
              background: "linear-gradient(180deg, #FFFFFF 0%, #FBFEFF 100%)",
              boxShadow: "0 12px 28px rgba(10, 28, 44, 0.1)",
              willChange: "transform, width, height, left",
              transformOrigin:
                desktopPanelDirection === 0
                  ? "center top"
                  : desktopPanelDirection > 0
                    ? "left top"
                    : "right top",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              ref={desktopPanelContentRef}
              className="min-w-0"
              style={{
                width: desktopPanelContentWidth,
                willChange: "opacity, transform",
              }}
            >
              <motion.div
                key={desktopVisiblePanel}
                initial={desktopPanelContentInitial}
                animate={{
                  opacity: 1,
                  x: 0,
                  y: 0,
                  scale: 1,
                }}
                transition={{
                  opacity: DESKTOP_PANEL_CONTENT_ENTER_TRANSITION,
                  x: DESKTOP_PANEL_CONTENT_ENTER_TRANSITION,
                  y: DESKTOP_PANEL_CONTENT_ENTER_TRANSITION,
                  scale: { duration: 0.26, ease: [0.18, 1, 0.3, 1] },
                }}
                style={{ width: "100%" }}
              >
                {activeDesktopPanelContent}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {isMounted &&
        showFlexibleDatesTooltip &&
        flexibleTooltipPosition &&
        createPortal(
          <div
            className="fixed z-[100010] w-[232px] rounded-2xl border px-3 py-2.5 text-left text-[11px] leading-[1.5] shadow-xl"
            style={{
              top: flexibleTooltipPosition.top,
              left: flexibleTooltipPosition.left,
              borderColor: "#D8F1F8",
              background: "linear-gradient(180deg, #FFFFFF 0%, #FBFEFF 100%)",
              color: STAYS_TEXT_MUTED,
              boxShadow: "0 16px 32px rgba(10, 28, 44, 0.12)",
            }}
            onMouseEnter={clearFlexibleTooltipHideTimeout}
            onMouseLeave={scheduleFlexibleTooltipHide}
          >
            {FLEXIBLE_DATES_TOOLTIP}
          </div>,
          document.body,
        )}
    </div>
  )
}

ListingSearch.displayName = "ListingSearch"

export default ListingSearch
